import { CloudUpload, Info, TextFields } from '@mui/icons-material';
import { Alert, Box, Button, Card, CardContent, Divider, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EEntityType } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import AnalyseSkillsTextButton from '@src/App/Components/Buttons/AnalyseSkillsTextButton/AnalyseSkillsTextButton';
import AnalyseCvDialog from '@src/App/Components/Dialogs/AnalyseSkillsDialog/AnalyseCvDialog';
import CVAnalysisDialog from '@src/App/Components/Dialogs/CVAnalysisDialog/CVAnalysisDialog';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import AttachmentsTable from '@src/App/Components/Tables/AttachmentsTable/AttachmentsTable';
import type Attachment from '@src/Objects/Attachment';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

import { ProfileTabHeader } from './ProfileTabHeader';

/**
 * Function to analyze the CV tab.
 * @returns {JSX.Element} - The analyzed CV tab.
 */
export function AnalyseCvTab(): JSX.Element {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).profileSettings;
    const { openDialog } = useDialog();
    const employee = useSelector((state: AppState) => state.employee);

    // State to track uploaded file IDs and loading state
    const [uploadedFileIds, setUploadedFileIds] = React.useState<string[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const [cvDialogOpen, setCvDialogOpen] = React.useState(false);

    // Handle file upload completion
    const handleUploadFinished = (attachment: Attachment) => {
        console.log('File uploaded with ID:', attachment.id);
        setUploadedFileIds((prev) => [...prev, attachment.id]);
        setIsLoading(true);
    };

    const handleAttachmentFileContent = (attachment: Attachment, fileContent: string) => {
        console.log(`File content updated: ${attachment.id}`, fileContent);
        if (uploadedFileIds.includes(attachment.id)) {
            setIsLoading(false);
            openDialog(
                <AnalyseCvDialog
                    id="AnalyseCvDialog"
                    initialLoadingText=""
                    fileContent={fileContent}
                    resolve={() => {
                        return;
                    }}
                />,
            );
        }
    };

    const handleNewAnalysis = () => {
        setCvDialogOpen(true);
    };

    const handleCvAnalysisComplete = (results: any) => {
        console.log('CV Analysis completed:', results);
        // Handle analysis results - could update profile data
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <ProfileTabHeader title={langStrings.analyseTitle} subtitle={langStrings.analyseDescription} />

            {/* Info Box */}
            <Alert
                severity="info"
                icon={<Info />}
                sx={{
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    border: `1px solid ${theme.palette.info.main}`,
                    '& .MuiAlert-message': { width: '100%' },
                }}
            >
                <Typography variant="body2">
                    Laden Sie Ihre Dokumente hoch und lassen Sie unsere KI automatisch Skills, Zertifikate und
                    Erfahrungen extrahieren. Manuelles Hinzufügen ist über die jeweiligen Tabs (Skills, Zertifikate)
                    möglich.
                </Typography>
            </Alert>

            {/* Analysis Options Card */}
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Analyse-Optionen
                    </Typography>

                    {/* CV Analysis Section */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <CloudUpload sx={{ color: theme.palette.primary.main, fontSize: 24 }} aria-hidden="true" />
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    CV Analyse
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Laden Sie Ihren Lebenslauf hoch und lassen Sie unsere KI automatisch Skills,
                                    Zertifikate und Rollen extrahieren.
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<CloudUpload />}
                            onClick={handleNewAnalysis}
                            size="medium"
                            sx={{
                                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                                px: 3,
                                py: 1.5,
                            }}
                            aria-label="CV-Analyse starten"
                        >
                            CV Analyse
                        </Button>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Text Analysis Section */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <TextFields sx={{ color: theme.palette.primary.main, fontSize: 24 }} aria-hidden="true" />
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Text Analyse
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Fügen Sie manuell Text ein und lassen Sie diesen auf Skills und Kompetenzen
                                    analysieren.
                                </Typography>
                            </Box>
                        </Box>
                        <AnalyseSkillsTextButton />
                    </Box>

                    {isLoading && (
                        <Box sx={{ mt: 3 }}>
                            <LoadingBox text={langStrings.analysingFile} />
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* File Server - Hochgeladene Dokumente */}
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        File Server - Hochgeladene Dokumente
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Verwalten Sie alle Ihre Dokumente mit erweiterten Analyse-Funktionen und Backend-Integration.
                        Upload, Kategorisierung und automatische Analyse.
                    </Typography>
                    {employee && (
                        <AttachmentsTable
                            entityId={employee.id}
                            entityType={EEntityType.EMPLOYEE}
                            uploadFinished={handleUploadFinished}
                            onAttachmentFileContent={handleAttachmentFileContent}
                        />
                    )}
                </CardContent>
            </Card>

            {/* CV Analysis Dialog */}
            <CVAnalysisDialog
                open={cvDialogOpen}
                onClose={() => setCvDialogOpen(false)}
                onAnalysisComplete={handleCvAnalysisComplete}
            />
        </Box>
    );
}
